import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BookOpen, Video, ArrowLeft, Clock, FileText } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.azalega-academy.com';
const API_URL = API_BASE.endsWith('/api/v1') ? API_BASE : `${API_BASE}/api/v1`;

const IndicatorDetailPage = () => {
    const { indicatorId } = useParams();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);

    // Icon mapping for indicators
    const indicatorIcons = {
        'sft': '📊',
        'oms': '🎯',
        'ste': '⚡',
        'obp': '📈'
    };

    // State for related courses
    const [videoCourseId, setVideoCourseId] = useState(null);
    const [ebookCourseId, setEbookCourseId] = useState(null);

    useEffect(() => {
        fetchIndicatorCourse();
    }, [indicatorId]);

    const fetchIndicatorCourse = async () => {
        try {
            // 1. Fetch the requested course
            const response = await axios.get(`${API_URL}/courses/${indicatorId}`);
            const mainCourse = response.data.course || response.data;
            setCourse(mainCourse);

            // 2. Determine if this is video or ebook
            // 2. Determine if this is video or ebook (or both)
            if (mainCourse.courseType === 'video' || (mainCourse.modules && mainCourse.modules.some(m => m.lessons && m.lessons.length > 0))) {
                setVideoCourseId(mainCourse._id);
            }

            if (mainCourse.courseType === 'ebook' || mainCourse.ebookFile) {
                setEbookCourseId(mainCourse._id);
            }

            // 3. If it has an indicatorName, fetch related courses to find the pair
            if (mainCourse.indicatorName) {
                const relatedResponse = await axios.get(`${API_URL}/courses`, {
                    params: {
                        category: 'sistem',
                        published: true,
                        limit: 100
                    }
                });

                const allIndicators = relatedResponse.data.courses || relatedResponse.data || [];

                // Find courses with SAME indicatorName (case insensitive, trimmed)
                const relatedCourses = allIndicators.filter(c =>
                    c.indicatorName &&
                    c.indicatorName.trim().toUpperCase() === mainCourse.indicatorName.trim().toUpperCase()
                );

                // Assign IDs based on type
                // Assign IDs based on content capabilities (Hybrid support)
                relatedCourses.forEach(c => {
                    const hasVideo = c.courseType === 'video' || (c.modules && c.modules.some(m => m.lessons && m.lessons.length > 0));
                    const hasEbook = c.courseType === 'ebook' || c.ebookFile;

                    if (hasVideo) setVideoCourseId(c._id);
                    if (hasEbook) setEbookCourseId(c._id);
                });
            } else {
                // Fallback for courses without indicatorName (check internal modules)
                const hasVideo = mainCourse.modules?.some(m => m.lessons?.some(l => l.type === 'video'));
                const hasEbook = mainCourse.modules?.some(m => m.lessons?.some(l => l.type === 'document')) || mainCourse.courseType === 'ebook' || mainCourse.ebookFile;

                if (hasVideo) setVideoCourseId(mainCourse._id);
                if (hasEbook) setEbookCourseId(mainCourse._id);
            }

            setLoading(false);
        } catch (error) {
            console.error('Error fetching course:', error);
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!course) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Course not found</h2>
                    <Link to="/sistem/indicator" className="text-blue-600 dark:text-blue-400 hover:text-blue-700">
                        ← Back to Indicators
                    </Link>
                </div>
            </div>
        );
    }

    // Get indicator icon from tags or use defaults
    const indicatorTag = course.tags?.find(tag => ['sft', 'oms', 'ste', 'obp'].includes(tag.toLowerCase()));
    const icon = indicatorTag ? indicatorIcons[indicatorTag.toLowerCase()] : '📈';

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-black">
            {/* Header */}
            <div className="bg-[#ffffff] dark:bg-black border-b border-gray-200 dark:border-gray-800 py-6 px-6">
                <div className="max-w-5xl mx-auto">
                    <Link
                        to="/sistem/indicator"
                        className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
                    >
                        <ArrowLeft size={20} className="mr-2" />
                        Back to Indicators
                    </Link>

                    <div className="flex items-center">
                        <div className="bg-green-500 w-20 h-20 rounded-xl flex items-center justify-center text-4xl mr-6 shadow-lg">
                            {icon}
                        </div>
                        <div>
                            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">{course.title}</h1>
                            <p className="text-gray-600 dark:text-gray-400 text-lg whitespace-pre-line">{course.shortDescription || course.description}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Selection */}
            <div className="max-w-5xl mx-auto px-6 py-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                    Select Learning Material
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* EBOOK Card */}
                    <Link
                        to={ebookCourseId ? `/courses/${ebookCourseId}` : '#'}
                        className={`group bg-[#ffffff] dark:!bg-black rounded-2xl border-2 border-gray-200 dark:border-gray-800 p-8 text-center transition-all duration-300 ${ebookCourseId
                            ? 'hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-2xl hover:shadow-blue-500/20 cursor-pointer'
                            : 'opacity-50 cursor-not-allowed'
                            }`}
                    >
                        <div className="bg-blue-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                            <BookOpen size={48} className="text-blue-600" />
                        </div>

                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                            📖 EBOOK
                        </h3>

                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {ebookCourseId
                                ? 'Read comprehensive guide with detailed explanations and examples'
                                : 'Ebook not available yet'}
                        </p>

                        {ebookCourseId && (
                            <div className="flex items-center justify-center space-x-4 text-sm text-gray-500 mb-4">
                                <div className="flex items-center">
                                    <FileText size={16} className="mr-1" />
                                    <span>PDF / Chapters</span>
                                </div>
                            </div>
                        )}

                        <div className={`inline-flex items-center px-6 py-3 rounded-lg font-semibold transition-colors ${ebookCourseId
                            ? 'bg-blue-600 text-white group-hover:bg-blue-700'
                            : 'bg-gray-300 text-gray-500'
                            }`}>
                            {ebookCourseId ? 'Open Ebook →' : 'Coming Soon'}
                        </div>
                    </Link>

                    {/* VIDEO Card */}
                    <Link
                        to={videoCourseId ? `/courses/${videoCourseId}/learn` : '#'}
                        state={{ returnUrl: `/sistem/indicator/${indicatorId}` }}
                        className={`group bg-[#ffffff] dark:!bg-black rounded-2xl border-2 border-gray-200 dark:border-gray-800 p-8 text-center transition-all duration-300 ${videoCourseId
                            ? 'hover:border-purple-500 dark:hover:border-purple-500 hover:shadow-2xl hover:shadow-purple-500/20 cursor-pointer'
                            : 'opacity-50 cursor-not-allowed'
                            }`}
                    >
                        <div className="bg-purple-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                            <Video size={48} className="text-purple-600" />
                        </div>

                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                            🎥 VIDEO
                        </h3>

                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {videoCourseId
                                ? 'Watch step-by-step video tutorial with practical demonstrations'
                                : 'Video not available yet'}
                        </p>

                        {videoCourseId && (
                            <div className="flex items-center justify-center space-x-4 text-sm text-gray-500 mb-4">
                                <div className="flex items-center">
                                    <Clock size={16} className="mr-1" />
                                    <span>HD Quality</span>
                                </div>
                            </div>
                        )}

                        <div className={`inline-flex items-center px-6 py-3 rounded-lg font-semibold transition-colors ${videoCourseId
                            ? 'bg-purple-600 text-white group-hover:bg-purple-700'
                            : 'bg-gray-300 text-gray-500'
                            }`}>
                            {videoCourseId ? 'Watch Video →' : 'Coming Soon'}
                        </div>
                    </Link>
                </div>

                {/* Info Box */}
                <div className="mt-12 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-900/30 rounded-xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">💡 Learning Tips</h3>
                    <ul className="text-gray-700 dark:text-gray-300 space-y-2">
                        <li className="flex items-start">
                            <span className="text-blue-600 dark:text-blue-400 mr-2">1.</span>
                            <span><strong>Start with Ebook</strong> - Understand the theory and concepts first</span>
                        </li>
                        <li className="flex items-start">
                            <span className="text-blue-600 dark:text-blue-400 mr-2">2.</span>
                            <span><strong>Watch Video</strong> - See practical application and real examples</span>
                        </li>
                        <li className="flex items-start">
                            <span className="text-blue-600 dark:text-blue-400 mr-2">3.</span>
                            <span><strong>Practice</strong> - Apply on demo account before real trading</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default IndicatorDetailPage;
